import React, { useState, useRef } from 'react';
import MobileReportOrientationManager from './reports/MobileReportOrientationManager';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import MarketingReport from './MarketingReport';
import { ReportData } from '../types/reportTypes';
import {
  ReportHeaderContainer,
  ReportSection
} from './reports/ReportResponsiveStyles';

// Logo options for customization
const COMPANY_LOGOS = {
  ROI_EXTERIORS: 'https://roiconstruction.com/wp-content/uploads/2024/04/Marylands-Premier-Roofer-ROI-Construction-Roofing-Siding-Windows-Doors.png',
  ROI_CONSTRUCTION: 'https://roiconstruction.com/wp-content/uploads/2023/03/ROI-Construction-logo.png',
  // Add more logos as needed
};

// PDF Layout options
export enum PDFLayout {
  PORTRAIT = 'portrait',
  LANDSCAPE = 'landscape'
}

// Page size options
export enum PageSize {
  LETTER = 'letter',
  A4 = 'a4',
  LEGAL = 'legal'
}

// Branding themes
export enum BrandingTheme {
  DEFAULT = 'default',
  MINIMAL = 'minimal',
  CORPORATE = 'corporate'
}

interface PDFExportOptions {
  layout: PDFLayout;
  pageSize: PageSize;
  brandingTheme: BrandingTheme;
  includeHeader: boolean;
  includeFooter: boolean;
  includeCompanyLogo: boolean;
  companyLogo: string;
  companyName: string;
  documentTitle: string;
  includeTimestamp: boolean;
  includePageNumbers: boolean;
  customCSS?: string;
  customHeaderText?: string;
  customFooterText?: string;
  margin: number;
}

interface ReportWithPDFExportProps {
  data: ReportData;
  initialOptions?: Partial<PDFExportOptions>;
}

const defaultOptions: PDFExportOptions = {
  layout: PDFLayout.PORTRAIT,
  pageSize: PageSize.LETTER,
  brandingTheme: BrandingTheme.DEFAULT,
  includeHeader: true,
  includeFooter: true,
  includeCompanyLogo: true,
  companyLogo: COMPANY_LOGOS.ROI_EXTERIORS,
  companyName: 'ROI Exteriors',
  documentTitle: 'Marketing Performance Report',
  includeTimestamp: true,
  includePageNumbers: true,
  margin: 20,
};

/**
 * Enhanced ReportWithPDFExport component with configurable layout, branding and headers/footers
 */
const ReportWithPDFExport: React.FC<ReportWithPDFExportProps> = ({ 
  data, 
  initialOptions 
}) => {
  const [pdfOptions, setPdfOptions] = useState<PDFExportOptions>({
    ...defaultOptions,
    ...initialOptions
  });
  const [isExporting, setIsExporting] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  // Function to generate PDF with enhanced options
  const generatePDF = async () => {
    if (!reportRef.current) return;
    
    setIsExporting(true);
    
    try {
      // Create a timestamp for the filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `${pdfOptions.companyName.replace(/\s+/g, '-')}-Marketing-Report-${timestamp}.pdf`;
      
      // Get page dimensions based on selected size and orientation
      const getPageDimensions = () => {
        switch (pdfOptions.pageSize) {
          case PageSize.LETTER:
            return pdfOptions.layout === PDFLayout.PORTRAIT ? [216, 279] : [279, 216];
          case PageSize.A4:
            return pdfOptions.layout === PDFLayout.PORTRAIT ? [210, 297] : [297, 210];
          case PageSize.LEGAL:
            return pdfOptions.layout === PDFLayout.PORTRAIT ? [216, 356] : [356, 216];
          default:
            return [216, 279]; // Default to letter portrait
        }
      };
      
      const [pageWidth, pageHeight] = getPageDimensions();
      
      // Create a new jsPDF instance with the selected options
      const doc = new jsPDF({
        orientation: pdfOptions.layout,
        unit: 'mm',
        format: pdfOptions.pageSize
      });
      
      // Set document properties
      doc.setProperties({
        title: pdfOptions.documentTitle,
        subject: `${pdfOptions.companyName} Marketing Performance Report`,
        author: pdfOptions.companyName,
        creator: 'ROI Marketing Portal'
      });
      
      // Function to add header to pages
      const addHeader = (pageNum: number) => {
        if (!pdfOptions.includeHeader) return;
        
        // Add header content
        doc.setFillColor(250, 250, 250);
        doc.rect(0, 0, pageWidth, 20, 'F');
        doc.setDrawColor(200, 200, 200);
        doc.line(0, 20, pageWidth, 20);
        
        // Add logo if enabled
        if (pdfOptions.includeCompanyLogo) {
          doc.addImage(pdfOptions.companyLogo, 'PNG', 10, 5, 30, 10);
        }
        
        // Add custom header text or default title
        const headerText = pdfOptions.customHeaderText || pdfOptions.documentTitle;
        doc.setFontSize(12);
        doc.setTextColor(50, 50, 50);
        doc.text(
          headerText,
          pdfOptions.includeCompanyLogo ? 45 : 10,
          12
        );
      };
      
      // Function to add footer to pages
      const addFooter = (pageNum: number, totalPages: number) => {
        if (!pdfOptions.includeFooter) return;
        
        doc.setFillColor(250, 250, 250);
        doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
        doc.setDrawColor(200, 200, 200);
        doc.line(0, pageHeight - 15, pageWidth, pageHeight - 15);
        
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        
        // Add timestamp if enabled
        if (pdfOptions.includeTimestamp) {
          const timestampText = `Generated on: ${new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}`;
          doc.text(timestampText, 10, pageHeight - 8);
        }
        
        // Add custom footer text or default
        const footerText = pdfOptions.customFooterText || 
          `${pdfOptions.companyName} - Confidential Marketing Report`;
        doc.text(footerText, pageWidth / 2, pageHeight - 8, { align: 'center' });
        
        // Add page numbers if enabled
        if (pdfOptions.includePageNumbers) {
          doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - 20, pageHeight - 8);
        }
      };
      
      // Apply custom CSS for branding theme if needed
      let customStyle: HTMLStyleElement | null = null;
      if (pdfOptions.brandingTheme !== BrandingTheme.DEFAULT || pdfOptions.customCSS) {
        customStyle = document.createElement('style');
        customStyle.type = 'text/css';
        
        // Apply theme CSS based on selected theme
        let themeCSS = '';
        switch (pdfOptions.brandingTheme) {
          case BrandingTheme.MINIMAL:
            themeCSS = `
              .bg-blue-50, .bg-green-50, .bg-orange-50, .bg-purple-50 { background-color: #ffffff !important; }
              h2.text-2xl { border-bottom: 1px solid #000 !important; }
              table th { background-color: #f8f8f8 !important; }
            `;
            break;
          case BrandingTheme.CORPORATE:
            themeCSS = `
              .bg-blue-50 { background-color: #E6F0FF !important; }
              .bg-green-50 { background-color: #E6FFF0 !important; }
              .bg-orange-50 { background-color: #FFF6E6 !important; }
              .bg-purple-50 { background-color: #F3E6FF !important; }
              h2.text-2xl { border-bottom: 2px solid #4A5568 !important; color: #2D3748 !important; }
              .font-bold { font-weight: 700 !important; }
            `;
            break;
          default:
            themeCSS = '';
        }
        
        // Add custom CSS if provided
        if (pdfOptions.customCSS) {
          themeCSS += pdfOptions.customCSS;
        }
        
        customStyle.innerHTML = themeCSS;
        document.head.appendChild(customStyle);
      }
      
      // Render the content to canvas
      const canvas = await html2canvas(reportRef.current, {
        scale: 2, // Higher resolution
        useCORS: true, // Allow images from different origins
        logging: false,
        backgroundColor: '#ffffff',
      });
      
      // Calculate dimensions and pagination
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pageWidth - (pdfOptions.margin * 2);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Calculate total pages needed
      const contentAreaHeight = pageHeight - (pdfOptions.includeHeader ? 25 : 5) - 
                                (pdfOptions.includeFooter ? 20 : 5);
      const totalPages = Math.ceil(imgHeight / contentAreaHeight);
      
      // Add content across multiple pages
      let remainingHeight = imgHeight;
      let sourceY = 0;
      
      for (let i = 0; i < totalPages; i++) {
        // Add a new page for pages after the first
        if (i > 0) {
          doc.addPage();
        }
        
        // Add header and footer
        addHeader(i + 1);
        addFooter(i + 1, totalPages);
        
        // Calculate the portion of the image to render on this page
        const contentY = pdfOptions.includeHeader ? 25 : 5;
        const printHeight = Math.min(remainingHeight, contentAreaHeight);
        
        // Add a portion of the image to this page
        // Add a portion of the image to this page with correct arguments
        doc.addImage({
          imageData: imgData,
          format: 'PNG',
          x: pdfOptions.margin,
          y: contentY,
          width: imgWidth,
          height: printHeight,
          alias: `page-${i}`,
          compression: 'FAST',
          rotation: 0
        });
        
        // Update remaining height and source position
        remainingHeight -= contentAreaHeight;
        sourceY += canvas.height * (contentAreaHeight / imgHeight);
      }
      
      // Save the PDF
      doc.save(filename);
      
      // Remove custom styles if added
      if (customStyle) {
        document.head.removeChild(customStyle);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };
  
  // Update PDF options
  const updateOption = <K extends keyof PDFExportOptions>(
    option: K, 
    value: PDFExportOptions[K]
  ) => {
    setPdfOptions(prev => ({
      ...prev,
      [option]: value
    }));
  };
  
  // Reset options to default
  const resetOptions = () => {
    setPdfOptions(defaultOptions);
  };
  
  // Options panel component
  const OptionsPanel = () => (
    <div className="bg-white shadow-md rounded-lg p-4 mb-4">
      <h3 className="text-lg font-semibold mb-2">PDF Export Options</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Layout & Size Section */}
        <div>
          <h4 className="font-medium mb-2">Layout & Size</h4>
          
          <div className="mb-2">
            <label className="block text-sm mb-1">Page Orientation</label>
            <select 
              value={pdfOptions.layout}
              onChange={(e) => updateOption('layout', e.target.value as PDFLayout)}
              className="w-full p-2 border rounded"
            >
              <option value={PDFLayout.PORTRAIT}>Portrait</option>
              <option value={PDFLayout.LANDSCAPE}>Landscape</option>
            </select>
          </div>
          
          <div className="mb-2">
            <label className="block text-sm mb-1">Page Size</label>
            <select 
              value={pdfOptions.pageSize}
              onChange={(e) => updateOption('pageSize', e.target.value as PageSize)}
              className="w-full p-2 border rounded"
            >
              <option value={PageSize.LETTER}>Letter</option>
              <option value={PageSize.A4}>A4</option>
              <option value={PageSize.LEGAL}>Legal</option>
            </select>
          </div>
          
          <div className="mb-2">
            <label className="block text-sm mb-1">Margin (mm)</label>
            <input 
              type="number" 
              min="0" 
              max="50"
              value={pdfOptions.margin}
              onChange={(e) => updateOption('margin', parseInt(e.target.value))}
              className="w-full p-2 border rounded"
            />
          </div>
        </div>
        
        {/* Branding Section */}
        <div>
          <h4 className="font-medium mb-2">Branding</h4>
          
          <div className="mb-2">
            <label className="block text-sm mb-1">Company Name</label>
            <input 
              type="text"
              value={pdfOptions.companyName}
              onChange={(e) => updateOption('companyName', e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>
          
          <div className="mb-2">
            <label className="block text-sm mb-1">Document Title</label>
            <input 
              type="text"
              value={pdfOptions.documentTitle}
              onChange={(e) => updateOption('documentTitle', e.target.value)}
              className="w-full p-2 border rounded"
            />
          </div>
          
          <div className="mb-2">
            <label className="block text-sm mb-1">Branding Theme</label>
            <select 
              value={pdfOptions.brandingTheme}
              onChange={(e) => updateOption('brandingTheme', e.target.value as BrandingTheme)}
              className="w-full p-2 border rounded"
            >
              <option value={BrandingTheme.DEFAULT}>Standard</option>
              <option value={BrandingTheme.MINIMAL}>Minimal</option>
              <option value={BrandingTheme.CORPORATE}>Corporate</option>
            </select>
          </div>
        </div>
      </div>
      
      {/* Logo Options */}
      <div className="mt-4">
        <h4 className="font-medium mb-2">Logo & Branding</h4>
        
        <div className="flex items-center mb-2">
          <input 
            type="checkbox"
            id="includeCompanyLogo"
            checked={pdfOptions.includeCompanyLogo}
            onChange={(e) => updateOption('includeCompanyLogo', e.target.checked)}
            className="mr-2"
          />
          <label htmlFor="includeCompanyLogo" className="text-sm">Include Company Logo</label>
        </div>
        
        {pdfOptions.includeCompanyLogo && (
          <div className="mb-2">
            <label className="block text-sm mb-1">Select Logo</label>
            <select 
              value={pdfOptions.companyLogo}
              onChange={(e) => updateOption('companyLogo', e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value={COMPANY_LOGOS.ROI_EXTERIORS}>ROI Exteriors</option>
              <option value={COMPANY_LOGOS.ROI_CONSTRUCTION}>ROI Construction</option>
            </select>
          </div>
        )}
      </div>
      
      {/* Header & Footer Options */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium mb-2">Header Options</h4>
          
          <div className="flex items-center mb-2">
            <input 
              type="checkbox"
              id="includeHeader"
              checked={pdfOptions.includeHeader}
              onChange={(e) => updateOption('includeHeader', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="includeHeader" className="text-sm">Include Header</label>
          </div>
          
          {pdfOptions.includeHeader && (
            <div className="mb-2">
              <label className="block text-sm mb-1">Custom Header Text (optional)</label>
              <input 
                type="text"
                value={pdfOptions.customHeaderText || ''}
                onChange={(e) => updateOption('customHeaderText', e.target.value)}
                placeholder="Leave blank to use document title"
                className="w-full p-2 border rounded"
              />
            </div>
          )}
        </div>
        
        <div>
          <h4 className="font-medium mb-2">Footer Options</h4>
          
          <div className="flex items-center mb-2">
            <input 
              type="checkbox"
              id="includeFooter"
              checked={pdfOptions.includeFooter}
              onChange={(e) => updateOption('includeFooter', e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="includeFooter" className="text-sm">Include Footer</label>
          </div>
          
          {pdfOptions.includeFooter && (
            <>
              <div className="mb-2">
                <label className="block text-sm mb-1">Custom Footer Text (optional)</label>
                <input 
                  type="text"
                  value={pdfOptions.customFooterText || ''}
                  onChange={(e) => updateOption('customFooterText', e.target.value)}
                  placeholder="Leave blank to use default footer"
                  className="w-full p-2 border rounded"
                />
              </div>
              
              <div className="flex items-center mb-2">
                <input 
                  type="checkbox"
                  id="includeTimestamp"
                  checked={pdfOptions.includeTimestamp}
                  onChange={(e) => updateOption('includeTimestamp', e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="includeTimestamp" className="text-sm">Include Generation Timestamp</label>
              </div>
              
              <div className="flex items-center mb-2">
                <input 
                  type="checkbox"
                  id="includePageNumbers"
                  checked={pdfOptions.includePageNumbers}
                  onChange={(e) => updateOption('includePageNumbers', e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="includePageNumbers" className="text-sm">Include Page Numbers</label>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex justify-end mt-4 space-x-2">
        <button 
          onClick={resetOptions}
          className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded"
        >
          Reset Options
        </button>
        <button 
          onClick={() => setShowOptions(false)}
          className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded"
        >
          Close
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <ReportHeaderContainer className="justify-end">
        <div className="flex flex-wrap gap-2 mb-4">
          <button 
            onClick={() => setShowOptions(!showOptions)}
            className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
            </svg>
            PDF Options
          </button>
          
          <button 
            onClick={generatePDF}
            disabled={isExporting}
            className={`bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded flex items-center 
              ${isExporting ? 'opacity-75 cursor-not-allowed' : ''}`}
          >
            {isExporting ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4" 
                    fill="none" 
                  />
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" 
                  />
                </svg>
                Generating PDF...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z" clipRule="evenodd" />
                </svg>
                Download PDF Report
              </>
            )}
          </button>
          
        </div>
      </ReportHeaderContainer>
      
      {showOptions && <OptionsPanel />}
      
      <ReportSection>
        <div ref={reportRef}>
          <MobileReportOrientationManager>
          <MarketingReport data={data} />
          </MobileReportOrientationManager>
        </div>
      </ReportSection>
    </div>
  );
};

export default ReportWithPDFExport;